/**
 * Lightweight audio player for the fine alignment modal
 */
export class MiniAudioPlayer {
  // DOM elements
  private audioElement: HTMLAudioElement | null = null;
  
  // Playback state
  private _isPlaying: boolean = false;
  private _currentTime: number = 0;
  private startOffset: number = 0;
  private duration: number = 0;
  
  // Loop state
  private _loopEnabled: boolean = false;
  private _loopStart: number = 0;
  private _loopEnd: number = 0;
  private _playbackRate: number = 1.0;
  
  // Event callbacks
  onTimeUpdate: ((time: number) => void) | null = null;
  onPlaybackStateChange: ((isPlaying: boolean) => void) | null = null;
  onEnded: (() => void) | null = null;
  onError: ((error: Error) => void) | null = null;
  
  // Update interval for time tracking
  private updateInterval: number | null = null;
  
  /**
   * Creates a new MiniAudioPlayer
   */
  constructor() {
    this.createAudioElement();
  }
  
  /**
   * Creates the audio element
   */
  private createAudioElement(): void {
    this.audioElement = document.createElement('audio');
    this.audioElement.preload = 'auto';
    
    // Set up event listeners
    this.audioElement.addEventListener('play', () => {
      this._isPlaying = true;
      this.startTimeUpdates();
      if (this.onPlaybackStateChange) this.onPlaybackStateChange(true);
    });
    
    this.audioElement.addEventListener('pause', () => {
      this._isPlaying = false;
      this.stopTimeUpdates();
      if (this.onPlaybackStateChange) this.onPlaybackStateChange(false);
    });
    
    this.audioElement.addEventListener('ended', () => {
      this._isPlaying = false;
      this.stopTimeUpdates();
      if (this.onPlaybackStateChange) this.onPlaybackStateChange(false);
      if (this.onEnded) this.onEnded();
    });
    
    this.audioElement.addEventListener('error', (e) => {
      this._isPlaying = false;
      this.stopTimeUpdates();
      if (this.onPlaybackStateChange) this.onPlaybackStateChange(false);
      if (this.onError) this.onError(new Error('Audio playback error'));
    });
    
    // Handle time updates manually for better performance
    this.audioElement.addEventListener('timeupdate', () => {
      if (this.audioElement) {
        this._currentTime = this.startOffset + this.audioElement.currentTime;
      }
    });
    
    // Handle duration change
    this.audioElement.addEventListener('durationchange', () => {
      if (this.audioElement) {
        this.duration = this.audioElement.duration;
      }
    });
  }
  
  /**
   * Start periodic time updates
   */
  private startTimeUpdates(): void {
    this.stopTimeUpdates();
    
    this.updateInterval = window.setInterval(() => {
      if (this.audioElement && this.onTimeUpdate) {
        this._currentTime = this.startOffset + this.audioElement.currentTime;
        this.onTimeUpdate(this._currentTime);
        
        // Check for loop condition
        if (this._loopEnabled && this._currentTime >= this._loopEnd) {
            this.seekTo(this._loopStart);
        }
      }
    }, 30); // Update 30 times per second for smooth UI
  }
  
  /**
   * Stop periodic time updates
   */
  private stopTimeUpdates(): void {
    if (this.updateInterval) {
      window.clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
  
  /**
   * Load an audio file
   * 
   * @param url URL of the audio file
   * @param startTime Start time of the audio segment in seconds
   * @param endTime End time of the audio segment in seconds
   */
  async loadAudio(url: string, startTime: number = 0, endTime?: number): Promise<void> {
    if (!this.audioElement) {
      this.createAudioElement();
    }
    
    
    if (!url) {
      return Promise.reject(new Error('Invalid or empty audio URL'));
    }
    
    return new Promise((resolve, reject) => {
      if (!this.audioElement) {
        const error = new Error('Audio element not available');
        console.error('MiniAudioPlayer:', error);
        reject(error);
        return;
      }
      
      // Save the start offset
      this.startOffset = startTime;
      this._currentTime = startTime;
      
      // Set up event listeners for loading
      const onCanPlay = () => {
        // Immediately seek to the startOffset
        if (this.audioElement && this.startOffset > 0) {
          try {
            // We need to seek to the relative time (0 is the beginning of the audio file)
            this.audioElement.currentTime = 0;
            
            // Now seek to the actual startOffset
            setTimeout(() => {
              if (this.audioElement) {
                this.audioElement.currentTime = this.startOffset;
              }
            }, 100);
          } catch (seekErr) {
            console.error('MiniAudioPlayer: Error seeking to startOffset:', seekErr);
          }
        }
        
        this.audioElement?.removeEventListener('canplay', onCanPlay);
        this.audioElement?.removeEventListener('error', onError);
        resolve();
      };
      
      const onError = (e: Event) => {
        const errorDetails = this.audioElement?.error ? 
          `Code: ${this.audioElement.error.code}, Message: ${this.audioElement.error.message}` : 
          'Unknown error';
        
        console.error('MiniAudioPlayer: Error loading audio -', errorDetails);
        
        this.audioElement?.removeEventListener('canplay', onCanPlay);
        this.audioElement?.removeEventListener('error', onError);
        reject(new Error(`Failed to load audio: ${errorDetails}`));
      };
      
      this.audioElement.addEventListener('canplay', onCanPlay);
      this.audioElement.addEventListener('error', onError);
      
      try {
        // Set CORS attribute to handle cross-origin requests
        this.audioElement.crossOrigin = 'anonymous';
        
        // Load the audio
        this.audioElement.src = url;
        this.audioElement.load();
        
        // Add a timeout in case the audio never loads
        setTimeout(() => {
          if (this.audioElement && this.audioElement.readyState === 0) {
            console.error('MiniAudioPlayer: Audio load timeout - readyState still 0 after 10 seconds');
            this.audioElement.removeEventListener('canplay', onCanPlay);
            this.audioElement.removeEventListener('error', onError);
            reject(new Error('Audio loading timed out after 10 seconds'));
          }
        }, 10000);
      } catch (err) {
        console.error('MiniAudioPlayer: Exception during audio loading:', err);
        reject(err);
      }
    });
  }
  
  /**
   * Start or resume playback
   */
  play(): void {
    if (!this.audioElement) return;
    
    // Ensure we're at the right position when starting playback
    // If we haven't adjusted the position yet, or if we're at the beginning of the file
    // but should be at an offset, correct that before playing
    if (this.startOffset > 0 && this.audioElement.currentTime < this.startOffset) {
      this.audioElement.currentTime = this.startOffset;
    }
    
    this.audioElement.play().catch(error => {
      console.error('Error playing audio:', error);
      if (this.onError) this.onError(new Error('Failed to start playback'));
    });
  }
  
  /**
   * Pause playback
   */
  pause(): void {
    if (!this.audioElement) return;
    
    this.audioElement.pause();
  }
  
  /**
   * Seek to a specific time
   * 
   * @param time Absolute time in seconds
   */
  seekTo(time: number): void {
    if (!this.audioElement) return;
    
    // Calculate relative time in the audio element
    const relativeTime = Math.max(0, time - this.startOffset);
    
    // Seek to the relative time
    this.audioElement.currentTime = relativeTime;
    
    // Update current time
    this._currentTime = time;
    if (this.onTimeUpdate) this.onTimeUpdate(time);
  }
  
  /**
   * Get the current absolute time
   */
  get currentTime(): number {
    return this._currentTime;
  }
  
  /**
   * Get the playback state
   */
  get isPlaying(): boolean {
    return this._isPlaying;
  }
  
  /**
   * Set the playback rate
   */
  set playbackRate(rate: number) {
    this._playbackRate = rate;
    if (this.audioElement) {
      this.audioElement.playbackRate = rate;
    }
  }
  
  /**
   * Get the playback rate
   */
  get playbackRate(): number {
    return this._playbackRate;
  }
  
  /**
   * Get the duration of the loaded audio
   */
  getTotalDuration(): number {
    return this.duration;
  }
  
  /**
   * Set loop range and state
   * 
   * @param start Loop start time in seconds
   * @param end Loop end time in seconds
   * @param enabled Whether looping is enabled
   */
  setLoopRange(start: number, end: number, enabled: boolean = true): void {
    this._loopStart = start;
    this._loopEnd = end;
    this._loopEnabled = enabled;
    
  }
  
  /**
   * Toggle loop state
   */
  toggleLoop(): boolean {
    this._loopEnabled = !this._loopEnabled;
    return this._loopEnabled;
  }
  
  /**
   * Get loop state
   */
  get loopEnabled(): boolean {
    return this._loopEnabled;
  }
  
  /**
   * Get loop start time
   */
  get loopStart(): number {
    return this._loopStart;
  }
  
  /**
   * Get loop end time
   */
  get loopEnd(): number {
    return this._loopEnd;
  }
  
  /**
   * Clean up resources
   */
  cleanup(): void {
    this.stopTimeUpdates();
    
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement.remove();
      this.audioElement = null;
    }
    
    this.onTimeUpdate = null;
    this.onPlaybackStateChange = null;
    this.onEnded = null;
    this.onError = null;
  }
}